import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ArrowRight, Users, ClipboardList, Store, BarChart3, HeartPulse, Rocket } from "lucide-react";

const STOPS = [
  { key: "clients", icon: HeartPulse, to: "/app/coach/client-pulse" },
  { key: "plans", icon: ClipboardList, to: "/app/coach/plans" },
  { key: "offerings", icon: Store, to: "/app/coach/offerings" },
  { key: "circles", icon: Users, to: "/app/coach/circles" },
  { key: "analytics", icon: BarChart3, to: "/app/coach/analytics" },
];

export default function Tour() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("tour.meta_title"));

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="coach-tour-page">
      <Seo title={t("tour.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="tour-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("tour.eyebrow")} title={t("tour.title")} subtitle={t("tour.subtitle")} />

      <div className="space-y-3">
        {STOPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <button key={s.key} onClick={() => navigate(s.to)} data-testid={`tour-stop-${s.key}`} className="group flex w-full items-center gap-4 rounded-2xl border border-border bg-card p-4 text-left hover:bg-accent/40">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-muted-foreground">{t("tour.step_n", { n: i + 1 })}</p>
                <p className="mt-0.5 font-semibold text-foreground">{t(`tour.${s.key}_title`)}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{t(`tour.${s.key}_sub`)}</p>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-primary"><Rocket className="h-5 w-5" /></div>
        <p className="text-sm text-muted-foreground">{t("tour.cta_sub")}</p>
        <Link to="/app/coach/setup"><Button className="rounded-full" data-testid="tour-to-setup">{t("tour.cta")}</Button></Link>
      </div>
    </div>
  );
}
